import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, LogOut, Upload, Menu, X, UserCog } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProfiles } from "../context/ProfileContext";
import Avatar from "./Avatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { activeProfile, selectProfile } = useProfiles();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/browse?q=${encodeURIComponent(term)}` : "/browse");
    setOpen(false);
  };

  const switchProfile = () => {
    selectProfile(null);
    navigate("/profiles");
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center group" translate="no">
          <span className="notranslate font-display text-3xl sm:text-4xl tracking-wider leading-none logo-chrome" translate="no">
            KINE
          </span>
          <span className="notranslate font-display text-3xl sm:text-4xl tracking-wider leading-none logo-red ml-1" translate="no">
            BE
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-mono-mini uppercase tracking-widest">
          <Link to="/" data-testid="nav-home" className="text-zinc-300 hover:text-white transition-colors">
            Accueil
          </Link>
          <Link to="/browse?type=film" data-testid="nav-films" className="text-zinc-300 hover:text-white transition-colors">
            Films
          </Link>
          <Link to="/browse?type=serie" data-testid="nav-series" className="text-zinc-300 hover:text-white transition-colors">
            Séries
          </Link>
          <Link to="/browse" data-testid="nav-browse" className="text-zinc-300 hover:text-white transition-colors">
            Catalogue
          </Link>
          {user && user !== false && (
            <Link to="/my-list" data-testid="nav-mylist" className="text-zinc-300 hover:text-white transition-colors">
              Ma Liste
            </Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="text-[color:var(--ns-accent)] hover:text-white transition-colors flex items-center gap-1">
              <Upload size={14} /> Studio
            </Link>
          )}
        </nav>

        <form onSubmit={submit} className="hidden md:flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 w-72">
          <Search size={16} className="text-zinc-400" />
          <input
            data-testid="nav-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un titre, un casting…"
            className="bg-transparent outline-none text-sm text-white placeholder-zinc-500 w-full"
          />
        </form>

        <div className="flex items-center gap-3">
          {user === false ? (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline text-sm text-zinc-300 hover:text-white transition">
                Connexion
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-btn"
                className="text-sm px-4 py-2 rounded-full btn-accent"
              >
                S&apos;inscrire
              </Link>
            </>
          ) : user ? (
            <div className="flex items-center gap-3">
              {activeProfile && (
                <button
                  data-testid="nav-active-profile"
                  onClick={switchProfile}
                  className="flex items-center gap-2 pr-1"
                  title="Changer de profil"
                >
                  <Avatar profile={activeProfile} size={32} />
                  <span className="hidden sm:inline text-sm text-zinc-200">
                    {activeProfile.name}
                  </span>
                </button>
              )}
              <button
                data-testid="nav-switch-profile"
                onClick={switchProfile}
                className="p-2 rounded-full hover:bg-white/10 transition"
                title="Gérer les profils"
              >
                <UserCog size={16} />
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                className="p-2 rounded-full hover:bg-white/10 transition"
                title="Déconnexion"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : null}

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 rounded-full hover:bg-white/10"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 px-4 py-4 space-y-3">
          <form onSubmit={submit} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2">
            <Search size={16} className="text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className="bg-transparent outline-none text-sm text-white placeholder-zinc-500 w-full"
            />
          </form>
          <div className="flex flex-col gap-2 text-sm font-mono-mini uppercase tracking-widest">
            <Link to="/" onClick={() => setOpen(false)}>Accueil</Link>
            <Link to="/browse?type=film" onClick={() => setOpen(false)}>Films</Link>
            <Link to="/browse?type=serie" onClick={() => setOpen(false)}>Séries</Link>
            <Link to="/browse" onClick={() => setOpen(false)}>Catalogue</Link>
            {user && user !== false && <Link to="/my-list" onClick={() => setOpen(false)}>Ma Liste</Link>}
            {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}>Studio Admin</Link>}
          </div>
        </div>
      )}
    </header>
  );
}
